import * as Long from "long";
import { selectBalancingPurchaseOfferGroups, selectBalancingShopOfferByNameId, selectBalancingShopOfferGroupById, } from "../balancingSelectors/selectBalancingPurchaseOfferGroups";
import { selectPurchaseOfferShiftLevel } from "../balancingSelectors/selectBalancingPurchaseOfferShiftLevels";
import { selectResourceAmountInfo, selectResourceInfo, } from "../balancingSelectors/selectBalancingResources";
import { IdResource } from "../constants/IdResource";
import { OfferCategoryType, } from "../features/balancing/types/BalancingCore";
import { getResourceIcon } from "../features/generic/icon/getResourceIcon";
import { IdIcon } from "../features/generic/icon/IdIcon";
import { IdOfferType } from "../features/shop/IdOfferType";
import { mapCountryCodeToLegalInfo } from "../features/shop/mapCountryCodeToLegalInfo";
import { warnShop } from "../utils/logging/logging";
import { utilEvent } from "../utils/utilEvent";
import { utilShop } from "../utils/utilShop";
import { utilTime } from "../utils/utilTime";
import { selectOfferBuffAmount } from "./selectBuffsInfo";
import { selectContractInfo } from "./selectContractInfo";
import { selectPrimeTimeEventInfo } from "./selectEventInfo";
import { selectPlayerLevel } from "./selectPlayerLevelInfo";
export var HeadOfferPackSize = 2;
export var OfferDuplicationSize = 2;
export var HCOfferPackSize = 4;
export var applySubscriptionBuff = function (state, offers) {
    offers.forEach(function (value) {
        var baseGoldBuffAmount = selectOfferBuffAmount(state, IdResource.Gold, value.baseGoldValue);
        value.subscriptionBonus = Long.fromValue(baseGoldBuffAmount);
    });
    return offers;
};
export var selectPlayerHasSubscriptionBonus = function (state) {
    // 100 for rounding purposes... 1% of 1 = 0.
    return Long.fromValue(selectOfferBuffAmount(state, IdResource.Gold, 100)).gt(Long.ZERO);
};
export var selectShopAllOffers = function (state) {
    return selectShopHeadOffers(state).slice(0, HeadOfferPackSize).concat(selectShopBodyOffers(state), selectMandatoryOffers(state));
};
export var selectShopCountryCode = function (state) {
    var providers = selectShopProviders(state);
    return providers[0] != undefined ? providers[0].countryCode : "";
};
export var selectSmsOffers = function (state) {
    return state.shop.catalog.smsPackages || [];
};
export var selectShopProviders = function (state) {
    return state.shop.catalog.providers || [];
};
export var selectShopOffersFromGroupName = function (state, groupName) {
    var offerDuration = state.shop.characteristics.offerDuration;
    var _a = Array.from(selectBalancingPurchaseOfferGroups().entries()).find(function (_a) {
        var id = _a[0], group = _a[1];
        return group.name === groupName;
    }), offerGroupId = _a[0], offerGroup = _a[1];
    var offerList = offerGroup.purchaseOffer.map(function () {
        return { id: offerGroupId, duration: offerDuration };
    });
    var shiftLevelPackageParts = selectShopShiftLevelPackageParts(state);
    return selectShopOffers(state, offerList, shiftLevelPackageParts);
};
export var selectShopInterstitialOffers = function (state) {
    return [selectShopAllOffers(state)[0]];
};
export var tryMergeRewards = function (balancingValue, responseArray) {
    var matchingResponseItem = responseArray.find(function (element) { return element.typeId === balancingValue.id; });
    return {
        amount: matchingResponseItem ? matchingResponseItem.amount : balancingValue.amount,
        stageId: 1,
        typeId: balancingValue.id,
    };
};
export var selectShopLegalText = function (state) {
    var providers = state.shop.catalog.providers;
    if (providers == undefined ||
        providers.length === 0 ||
        providers[0].countryCode == undefined ||
        !mapCountryCodeToLegalInfo.has(providers[0].countryCode)) {
        return;
    }
    return mapCountryCodeToLegalInfo.get(providers[0].countryCode);
};
export var matchOfferValuesFromBalancing = function (rewards, serverItems) {
    return rewards
        .filter(function (val) { return val.typeId !== IdResource.Gold && val.id !== IdResource.Gold; })
        .map(function (element) {
        var merged = tryMergeRewards(element, serverItems);
        return selectResourceAmountInfo(merged.typeId, merged.amount || Long.ZERO);
    });
};
// TODO: add test
export var selectGrantRewards = function (state) {
    var offerId = state.grantRewards.rewards.paymentOfferId;
    var serverOfferItems = state.grantRewards.rewards.resourceRewards;
    var allOffers = applySubscriptionBuff(state, selectShopAllOffers(state)).slice();
    var _a = selectShopOfferBundles(state, allOffers), offerBundles = _a.offerBundles, offerBundleGrowthFund = _a.offerBundleGrowthFund;
    allOffers = allOffers.concat(offerBundles[0], offerBundleGrowthFund);
    var subscriptionBonusEnabled = selectPlayerHasSubscriptionBonus(state);
    var offer;
    if (allOffers && allOffers.length > 0) {
        offer = allOffers.find(function (value) { return value && value.purchaseOfferNameId === offerId; });
    }
    if (!offer) {
        var offerInfo = selectBalancingShopOfferByNameId(offerId);
        if (!offerInfo) {
            return {
                goldRewardAmount: Long.ZERO,
                offer: undefined,
                rewards: [],
                subscriptionBonusEnabled: false,
            };
        }
        var offerGroup = offerInfo[0], purchaseOffer = offerInfo[1];
        offer = offerGroupToOfferProps(state, {
            duration: 0,
            id: -1,
        }, offerGroup, {
            purchaseOffer: purchaseOffer,
            shopPackage: {},
        });
    }
    var rewards = matchOfferValuesFromBalancing(offer.rewards || [], serverOfferItems);
    // The first gold reward we receive from server is ALREADY the sum of base + web + bonus gold.
    var goldRewardAmount = serverOfferItems.find(function (val) { return val.typeId === IdResource.Gold; }).amount || Long.ZERO;
    return {
        goldRewardAmount: goldRewardAmount,
        offer: offer,
        rewards: rewards,
        subscriptionBonusEnabled: subscriptionBonusEnabled,
    };
};
export var selectShopShiftLevelTimestamp = function (state) {
    return state.shop.shiftLevel.remainingSeconds;
};
export var selectShopMinOfferTimestamp = function (state) {
    var _a = state.shop.characteristics, offerHead = _a.offerHead, offerDuration = _a.offerDuration;
    return (offerHead || []).reduce(function (acc, val) { return Math.min(acc, val.duration); }, offerDuration || Number.MAX_VALUE);
};
var selectMandatoryOffers = function (state) {
    var mandatoryOffers = [];
    var offerGroups = selectBalancingPurchaseOfferGroups();
    var shiftLevel = selectShopCurrentShiftLevel(state);
    Array.from(offerGroups.values())
        .filter(function (value) {
        return value.isTailOffer &&
            (value.availableToPlatforms == undefined ||
                value.availableToPlatforms.indexOf("WebBrowser") >= 0);
    })
        .forEach(function (value) {
        var offerGroup = selectShopOfferAfterReplacements(state, value);
        // Replacing with undefined = filtering
        if (offerGroup == undefined || offerGroup.purchaseOffer.length <= 0) {
            return;
        }
        if (offerGroup.offerCategory === OfferCategoryType.BundledOffer) {
            offerGroup.purchaseOffer.forEach(function (offer) {
                var offerProps = addOfferFromPurchaseOfferType(state, offerGroup, offer);
                if (offerProps != undefined) {
                    mandatoryOffers.push(offerProps);
                }
            });
        }
        else {
            var offer = offerGroup.purchaseOffer[shiftLevel] || offerGroup.purchaseOffer[0];
            var offerProps = addOfferFromPurchaseOfferType(state, offerGroup, offer);
            if (offerProps != undefined) {
                mandatoryOffers.push(offerProps);
            }
        }
    });
    return mandatoryOffers;
};
var addOfferFromPurchaseOfferType = function (state, offerGroup, offer) {
    var purchaseOfferInfo = selectShopPurchaseOfferInfoMandatory(state, offer);
    if (purchaseOfferInfo != undefined) {
        return offerGroupToOfferProps(state, {
            duration: -1,
            id: offerGroup.purchaseOfferCharacteristicId,
        }, offerGroup, purchaseOfferInfo);
    }
    return;
};
var selectShopBodyOffers = function (state) {
    var shiftLevelPackageParts = selectBodyOffersShiftLevel(state);
    var offers = selectValidBodyOffers(state);
    return selectShopOffers(state, offers, shiftLevelPackageParts);
};
var selectValidBodyOffers = function (state) {
    var _a = state.shop.characteristics, offerBody = _a.offerBody, nextOffeBody = _a.nextOffeBody, offerDuration = _a.offerDuration, nextOfferDuration = _a.nextOfferDuration;
    var useNextOffers = utilTime.secondsLeftTill(offerDuration) > 0;
    var currentOffers = useNextOffers ? offerBody : nextOffeBody;
    var currentDuration = useNextOffers ? offerDuration : nextOfferDuration;
    return (currentOffers || []).map(function (id) { return ({
        duration: currentDuration,
        id: id,
    }); });
};
var selectBodyOffersShiftLevel = function (state) {
    var shiftLevelPackageParts = selectShopShiftLevelPackageParts(state);
    for (var i = 0; i < HeadOfferPackSize; i++) {
        var enqueuedLevel = shiftLevelPackageParts.shift();
        shiftLevelPackageParts.push(enqueuedLevel);
    }
    return shiftLevelPackageParts;
};
var selectShopHeadOffers = function (state) {
    var offerHead = state.shop.characteristics.offerHead || [];
    if (offerHead.length === 0) {
        return [];
    }
    // Generate package order for flash sales (price tiers)
    var flashSaleShiftLevels = selectHeadOffersShiftLevel(state, offerHead);
    var offers = [];
    for (var i = 0; i < HeadOfferPackSize; i++) {
        var shiftLevelIndex = i % flashSaleShiftLevels.length;
        var newShiftLevel = flashSaleShiftLevels[shiftLevelIndex];
        var newOffer = selectShopOffers(state, [offerHead[0]], [newShiftLevel], HeadOfferPackSize - i);
        offers = offers.concat(newOffer);
    }
    return offers;
};
var selectHeadOffersShiftLevel = function (state, offers) {
    var shiftLevelPackageParts = selectShopShiftLevelPackageParts(state);
    var flashSaleShiftLevels = [];
    for (var i = 0; i < offers.length * HeadOfferPackSize; i++) {
        // alternating between first and second price tier.
        flashSaleShiftLevels[i] = shiftLevelPackageParts[i % HeadOfferPackSize];
    }
    return flashSaleShiftLevels;
};
var selectShopShiftLevelPackageParts = function (state) {
    var currentShiftLevel = selectShopCurrentShiftLevelInfo(state);
    if (currentShiftLevel == undefined) {
        return [];
    }
    return currentShiftLevel.packageOrder;
};
var selectShopOffers = function (state, offerList, shiftLevels, headOfferPackSize, offerDuplication) {
    if (headOfferPackSize === void 0) { headOfferPackSize = 1; }
    if (offerDuplication === void 0) { offerDuplication = 1; }
    var offers = [];
    for (var i = 0; i < offerList.length * headOfferPackSize; i++) {
        var idx = Math.round(i / offerDuplication);
        var offer = offerList[idx];
        if (!offer) {
            continue;
        }
        var offerInfo = selectOfferInfo(state, offer, shiftLevels, i);
        if (offerInfo != undefined) {
            offers.push(offerInfo);
        }
    }
    return offers;
};
var selectOfferInfo = function (state, offer, shiftLevels, shiftLevelIndex) {
    if (utilShop.isOfferExpired(offer)) {
        return;
    }
    var offerGroup = selectShopOfferAfterReplacements(state, selectBalancingShopOfferGroupById(offer.id));
    if (!offerGroup) {
        warnShop("Couldn't map offer " + offer.id + " to an offer group.");
        return;
    }
    var purchaseOfferInfo = selectShopPurchaseOfferInfo(state, offerGroup, shiftLevels, shiftLevelIndex);
    if (purchaseOfferInfo == undefined) {
        warnShop("Couldn't map offer " + offer.id + " to a package from shop.");
        return;
    }
    return offerGroupToOfferProps(state, offer, offerGroup, purchaseOfferInfo);
};
var isOfferGroupWebExclusive = function (offerGroup) {
    return offerGroup.name.indexOf("webExclusiveOffer") !== -1;
};
var offerGroupToOfferProps = function (state, offer, offerGroup, purchaseOfferInfo) {
    var purchaseOffer = purchaseOfferInfo.purchaseOffer, shopPackage = purchaseOfferInfo.shopPackage;
    var providers = selectShopProviders(state);
    var rewardList = (purchaseOffer.resourceReward || []).slice();
    var baseGoldValueIdx = rewardList.findIndex(function (val) { return val.resourceId === IdResource.Gold; });
    var baseGoldValue = baseGoldValueIdx !== -1 ? rewardList[baseGoldValueIdx].amount : 0;
    // Remove first gold value, because we pass it as a prop
    rewardList.splice(baseGoldValueIdx, 1);
    var rewards = rewardList.map(function (val) {
        return selectResourceAmountInfo(val.resourceId, val.amount);
    });
    // FIXME: this needs to be fixed with balancing + mobile.
    if (isOfferGroupWebExclusive(offerGroup)) {
        offerGroup.locaKeyTitle = offerGroup.locaKeyEyecatcher;
    }
    return {
        backgroundColor: offerGroup.backgroundColor,
        baseGoldValue: baseGoldValue,
        bonusGoldPercent: purchaseOffer.bonusGoldPercent,
        bonusGoldValue: purchaseOffer.bonusGoldValue,
        imageName: offerGroup.imageName,
        locaKeyEyecatcher: offerGroup.locaKeyEyecatcher,
        locaKeyFlavour: offerGroup.locaKeyFlavour,
        locaKeyTitle: offerGroup.locaKeyTitle,
        offerCharacteristicId: offerGroup.purchaseOfferCharacteristicId,
        providers: providers,
        purchaseOfferNameId: purchaseOffer.nameId,
        purchasePoints: purchaseOffer.purchasePoints,
        remainingSeconds: offer.duration || 0,
        rewards: rewards,
        shopPackage: shopPackage,
        type: IdOfferType.Normal,
        webBonusGold: purchaseOffer.webBonusGold,
    };
};
export var selectShopCurrentShiftLevel = function (state) {
    return state.shop.shiftLevel.shiftLevel || 0;
};
export var selectShopCurrentShiftLevelInfo = function (state) {
    var shiftLevel = selectShopCurrentShiftLevel(state);
    return selectPurchaseOfferShiftLevel(shiftLevel);
};
export var selectShopPackageById = function (state, packageId) {
    return selectShopPackages(state).find(function (shopPackage) { return shopPackage.packageId === packageId; });
};
export var selectShopPackages = function (state) {
    return state.shop.catalog.packages || [];
};
var selectShopPurchaseOfferInfoMandatory = function (state, purchaseOffer) {
    var packageIdFull = purchaseOffer.shopPackageIdPart.split("_")[0];
    var packageId = packageIdFull[packageIdFull.length - 1];
    var shopPackage = selectShopPackageById(state, packageId);
    if (shopPackage == undefined) {
        return;
    }
    return {
        purchaseOffer: purchaseOffer,
        shopPackage: shopPackage,
    };
};
var selectShopPurchaseOfferInfo = function (state, offerGroup, shiftLevelPackageParts, packagePartIndex) {
    // Determine used package from group
    var usedShopPackagePart = packagePartIndex < shiftLevelPackageParts.length
        ? shiftLevelPackageParts[packagePartIndex]
        : shiftLevelPackageParts[shiftLevelPackageParts.length - 1];
    var packageIdFull = usedShopPackagePart.split("_")[0];
    var packageId = packageIdFull[packageIdFull.length - 1];
    // Searching for offer which is using the wanted package
    for (var _i = 0, _a = offerGroup.purchaseOffer; _i < _a.length; _i++) {
        var purchaseOffer = _a[_i];
        if (purchaseOffer.shopPackageIdPart === usedShopPackagePart) {
            var shopPackage = selectShopPackageById(state, packageId);
            if (shopPackage != undefined) {
                return {
                    purchaseOffer: purchaseOffer,
                    shopPackage: shopPackage,
                };
            }
        }
    }
    return;
};
var selectShopOfferAfterReplacements = function (state, offerGroup) {
    if (!offerGroup) {
        return offerGroup;
    }
    var playerLevel = selectPlayerLevel(state);
    var contractInfo = selectContractInfo(state);
    var contractSlots = contractInfo ? contractInfo.length : 0;
    // Web replacement
    if (offerGroup.webReplacement != undefined) {
        var replacementId = offerGroup.webReplacement.replacementId;
        offerGroup = selectBalancingShopOfferGroupById(replacementId);
        if (!offerGroup) {
            warnShop("Couldn't map webReplacement offer " + replacementId + " to an offer group.");
            return;
        }
    }
    // Other replacement
    if (offerGroup.replacement != undefined) {
        offerGroup.replacement.some(function (replacement) {
            if (replacement.maxPlayerLevel != undefined &&
                playerLevel < replacement.maxPlayerLevel) {
                offerGroup = selectBalancingShopOfferGroupById(replacement.replacementId);
                return true;
            }
            if (replacement.minContractSlots != undefined &&
                contractSlots >= replacement.minContractSlots) {
                offerGroup = selectBalancingShopOfferGroupById(replacement.replacementId);
                return true;
            }
            // TODO: add Coop/Adventure checks once the features are implemented
            return false;
        });
    }
    return offerGroup;
};
export var selectShopOfferBundles = function (state, offers) {
    var offerBundles = [];
    var activeBundleIdx = -1;
    for (var i = 0; i < offers.length; i++) {
        var offer = offers[i];
        // Rewards length will be 0, because we remove the first gold reward in selector
        if (offer.rewards && offer.rewards.length === 0) {
            if (activeBundleIdx === -1) {
                activeBundleIdx = offerBundles.length;
            }
            var activeBundle = offerBundles[activeBundleIdx] || [];
            activeBundle.push(offer);
            offerBundles[activeBundleIdx] = activeBundle;
            offers.splice(i, 1);
            i--;
        }
        else {
            activeBundleIdx = -1;
        }
    }
    var offerBundleGrowthFund = offerBundles.shift() || [];
    if (offerBundleGrowthFund && offerBundleGrowthFund.length > 0) {
        offerBundleGrowthFund.forEach(function (value) { return (value.type = IdOfferType.GrowthFund); });
    }
    if (offerBundles && offerBundles.length > 0) {
        // Temporary solutions to restrict HC offer size to 4
        offerBundles[0] = offerBundles[0].slice(0, HCOfferPackSize);
        offerBundles[0] = selectHCOfferPrimeTimeBonus(state, offerBundles[0]);
        offerBundles[0].forEach(function (value) { return (value.type = IdOfferType.HCOffer); });
    }
    return {
        offerBundleGrowthFund: offerBundleGrowthFund,
        offerBundles: offerBundles,
    };
};
export var selectHCOfferPrimeTimeBonus = function (state, offerBundles) {
    var primeTimeEventInfo = selectPrimeTimeEventInfo(state);
    if (primeTimeEventInfo && primeTimeEventInfo.balancing && primeTimeEventInfo.balancing.effect) {
        var bonusGoldPercent_1 = primeTimeEventInfo.balancing.effect;
        offerBundles.forEach(function (value) {
            value.bonusGoldPercent = bonusGoldPercent_1;
            value.primeTimeBonus = value.baseGoldValue * (bonusGoldPercent_1 - 1);
            value.remainingSeconds = utilEvent.getEndTime(primeTimeEventInfo);
        });
    }
    else {
        offerBundles.forEach(function (value) {
            value.bonusGoldPercent = 0;
            value.primeTimeBonus = undefined;
            value.remainingSeconds = 0;
        });
    }
    return offerBundles;
};
export var selectGrowthFundCharacteristicIds = function (state) {
    var allOffers = selectShopAllOffers(state);
    var offerBundleGrowthFund = selectShopOfferBundles(state, allOffers).offerBundleGrowthFund;
    if (offerBundleGrowthFund.length > 0) {
        return offerBundleGrowthFund.map(function (offer) { return offer.offerCharacteristicId; });
    }
    return [];
};
export var selectGrowthFundRewardInfo = function (levelRewards) {
    var reward;
    if (levelRewards && levelRewards.length > 0) {
        var levelReward = levelRewards[0];
        if (levelReward.resourceReward && levelReward.resourceReward.length > 0) {
            reward = selectResourceInfo(levelReward.resourceReward[0].resourceId);
        }
    }
    return reward;
};
export var selectGrowthFundIcon = function (levelRewards) {
    var resource = selectGrowthFundRewardInfo(levelRewards);
    return resource ? getResourceIcon(resource) : IdIcon.GoldIcn;
};
export var selectSelectedOffer = function (state) {
    return state.shop.selectedOfferInfo.offer;
};
export var selectSelectedOfferPage = function (state) {
    return state.shop.selectedOfferInfo.page;
};
export var selectSelectedOfferInfo = function (state, offerCharacteristicId) {
    var allOffers = selectShopAllOffers(state);
    var _a = selectShopOfferBundles(state, allOffers), offerBundleGrowthFund = _a.offerBundleGrowthFund, offerBundles = _a.offerBundles;
    var offers = allOffers.concat(offerBundleGrowthFund, offerBundles);
    var selectedOfferInfo = {
        offer: undefined,
        page: 0,
    };
    offers.forEach(function (value, index) {
        if (value &&
            value.offerCharacteristicId &&
            value.offerCharacteristicId === offerCharacteristicId) {
            selectedOfferInfo = {
                offer: value,
                page: index,
            };
        }
    });
    (offerBundles[0] || []).forEach(function (value) {
        if (value &&
            value.offerCharacteristicId &&
            value.offerCharacteristicId === offerCharacteristicId) {
            selectedOfferInfo = {
                offer: value,
                page: offers.length - 1,
            };
        }
    });
    return selectedOfferInfo;
};



//////////////////
// WEBPACK FOOTER
// ./src/selectors/selectShopInfo.ts
// module id = 135
// module chunks = 3